import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Enquiry = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const countries = ['United States', 'Canada', 'United Kingdom', 'United Arab Emirates', 'Australia'];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/enquiries`, formData);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        country: '',
        message: ''
      });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('Enquiry error:', err);
      setError('Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16">
      <section className="py-32 bg-primary-dark text-text-inverse" data-testid="enquiry-hero">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4"
            data-testid="enquiry-subtitle"
          >
            Get In Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl lg:text-6xl font-bold mb-6 leading-tight"
            data-testid="enquiry-title"
          >
            Let's Grow Your Business <span className="italic text-secondary-main">Together</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-text-inverse/80 text-lg max-w-3xl mx-auto leading-relaxed"
            data-testid="enquiry-description"
          >
            Ready to transform your LinkedIn presence? Fill out the form below and our team will get back to you within 24 hours.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-background-main" data-testid="enquiry-form-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="enquiry-form-container"
            >
              <h2 className="font-heading text-3xl font-bold text-primary-main mb-8" data-testid="form-heading">
                Send Us a Message
              </h2>
              
              {submitted && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 flex items-center gap-3" data-testid="success-message">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-green-700">Thank you! We'll be in touch soon.</p>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700" data-testid="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name *"
                    data-testid="input-name"
                    className="w-full border-b border-black/20 bg-transparent py-3 px-0 focus:border-primary-main focus:outline-none transition-colors font-body"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address *"
                    data-testid="input-email"
                    className="w-full border-b border-black/20 bg-transparent py-3 px-0 focus:border-primary-main focus:outline-none transition-colors font-body"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder="Company Name *"
                    data-testid="input-company"
                    className="w-full border-b border-black/20 bg-transparent py-3 px-0 focus:border-primary-main focus:outline-none transition-colors font-body"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    data-testid="input-phone"
                    className="w-full border-b border-black/20 bg-transparent py-3 px-0 focus:border-primary-main focus:outline-none transition-colors font-body"
                  />
                </div>
                <div>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    data-testid="input-country"
                    className="w-full border-b border-black/20 bg-transparent py-3 px-0 focus:border-primary-main focus:outline-none transition-colors font-body text-text-secondary"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell us about your LinkedIn marketing needs *"
                    data-testid="input-message"
                    className="w-full border-b border-black/20 bg-transparent py-3 px-0 focus:border-primary-main focus:outline-none transition-colors font-body resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-button"
                  className="bg-primary-main text-white hover:bg-primary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
              data-testid="contact-info-container"
            >
              <div>
                <h2 className="font-heading text-3xl font-bold text-primary-main mb-8" data-testid="contact-heading">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4" data-testid="contact-email">
                    <div className="bg-secondary-main/10 p-3 rounded-full">
                      <Mail className="w-6 h-6 text-secondary-main" />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-primary-main mb-1">Email</h4>
                      <a href="mailto:infor@growthpop.in" className="text-text-secondary hover:text-secondary-main transition-colors">
                        infor@growthpop.in
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-testid="contact-phone">
                    <div className="bg-secondary-main/10 p-3 rounded-full">
                      <Phone className="w-6 h-6 text-secondary-main" />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-primary-main mb-1">Phone</h4>
                      <a href="tel:+917566125666" className="text-text-secondary hover:text-secondary-main transition-colors">
                        +91 75661 25666
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-testid="contact-whatsapp">
                    <div className="bg-secondary-main/10 p-3 rounded-full">
                      <MessageCircle className="w-6 h-6 text-secondary-main" />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-primary-main mb-1">WhatsApp</h4>
                      <a href="https://wa.me/917566125666" target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-secondary-main transition-colors">
                        +91 75661 25666
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-testid="contact-locations">
                    <div className="bg-secondary-main/10 p-3 rounded-full">
                      <MapPin className="w-6 h-6 text-secondary-main" />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-primary-main mb-1">Serving</h4>
                      <p className="text-text-secondary leading-relaxed">
                        Worldwide
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-primary-main/10 p-8" data-testid="business-hours">
                <h3 className="font-heading text-xl font-bold text-primary-main mb-4">Business Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Monday - Friday:</span>
                    <span className="text-text-primary font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Saturday:</span>
                    <span className="text-text-primary font-semibold">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Sunday:</span>
                    <span className="text-text-primary font-semibold">Closed</span>
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-4">* All times in EST</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enquiry;